import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import {
  Calculator, FlaskConical, BookOpen, Code, Music, Palette,
  Globe, Dumbbell, Microscope, Landmark, PenTool, Languages,
  ArrowRight, type LucideIcon,
} from "lucide-react";
import { fetchSubjects } from "@/services/AdminFetchServices";
import { cn } from "@/lib/utils";

function useReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add("visible"); observer.unobserve(el); } },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

// Home grid shows at most this many catalog subjects; "View all subjects" lists the rest.
const HOME_SUBJECT_LIMIT = 12;

const ICONS: Record<string, LucideIcon> = {
  mathematics: Calculator, maths: Calculator,
  science: FlaskConical, chemistry: FlaskConical, physics: FlaskConical,
  english: BookOpen, literature: BookOpen,
  programming: Code, "computer science": Code, coding: Code,
  music: Music, "art & design": PenTool, art: PenTool,
  languages: Languages, biology: Microscope,
  history: Landmark, geography: Globe, economics: Landmark,
  fitness: Dumbbell, "creative writing": Palette, writing: Palette,
};

// BMC palette: navy, teal, amber, blue, red — cycling across subjects
const GRADIENTS = [
  "from-[#262A7A] to-[#363A96]",
  "from-[#0E8A78] to-[#0a6d5e]",
  "from-[#F7B030] to-[#e09820]",
  "from-[#2F6BD8] to-[#1e55bb]",
  "from-[#D9472B] to-[#b83820]",
  "from-[#262A7A] to-[#363A96]",
  "from-[#0E8A78] to-[#0a6d5e]",
  "from-[#F7B030] to-[#e09820]",
  "from-[#2F6BD8] to-[#1e55bb]",
  "from-[#D9472B] to-[#b83820]",
  "from-[#262A7A] to-[#363A96]",
  "from-[#0E8A78] to-[#0a6d5e]",
];

interface CatalogSubject { id: string; name: string; }

export function SubjectBrowseSection() {
  const navigate = useNavigate();
  const gridRef = useReveal<HTMLDivElement>();

  // Same catalog the "View all subjects" page lists, so the two never disagree
  // (this grid used to be a hardcoded list of subjects not in the catalog).
  const [subjects, setSubjects] = useState<CatalogSubject[]>([]);
  useEffect(() => {
    fetchSubjects()
      .then((data: CatalogSubject[] | null) => setSubjects((data ?? []).slice(0, HOME_SUBJECT_LIMIT)))
      .catch(() => setSubjects([]));
  }, []);

  const handleSubject = (name: string) => {
    navigate(`/search?q=${encodeURIComponent(name)}`);
  };

  return (
    <section className="py-12 bg-white/20 backdrop-blur-sm relative overflow-hidden">
      <div className="container mx-auto px-4">
        <div ref={gridRef} className="flex gap-2 overflow-x-auto scrollbar-none reveal justify-center flex-wrap">
          {subjects.map((s, index) => {
            const Icon = ICONS[s.name.toLowerCase().trim()] ?? BookOpen;
            const grad = GRADIENTS[index % GRADIENTS.length];
            return (
              <button
                key={s.id}
                onClick={() => handleSubject(s.name)}
                className="group flex-none flex items-center gap-2 pl-1.5 pr-4 py-1.5 rounded-full bg-white border border-border/70 hover:border-[hsl(237,52%,31%)]/40 hover:shadow-sm transition-all duration-200 whitespace-nowrap"
              >
                <div className={cn("w-6 h-6 rounded-full flex items-center justify-center bg-gradient-to-br flex-shrink-0", grad)}>
                  <Icon className="w-3 h-3 text-white" />
                </div>
                <span className="text-xs font-medium text-foreground group-hover:text-[hsl(237,52%,31%)] transition-colors">{s.name}</span>
              </button>
            );
          })}
          <button
            onClick={() => navigate("/subjects")}
            className="flex-none flex items-center gap-1.5 pl-3 pr-4 py-1.5 rounded-full border border-dashed border-[hsl(237,52%,31%)]/40 text-xs font-medium text-[hsl(237,52%,45%)] hover:text-[hsl(237,52%,31%)] hover:border-[hsl(237,52%,31%)] transition-colors whitespace-nowrap"
          >
            View all
            <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    </section>
  );
}
